import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { 
  ArrowLeft, 
  Wifi,
  Smartphone,
  Zap,
  Droplets,
  Receipt,
  Trash2,
  Clock,
  CalendarClock,
  Sparkles,
  Check,
  X,
  AlertCircle,
  Settings
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";
import { v4 as uuidv4 } from "uuid";
import AutoPayModal from "../components/AutoPayModal";
import SchedulePaymentModal from "../components/SchedulePaymentModal";

// Provider icons mapping
const providerIcons = {
  "flow_ja": { icon: Wifi, color: "bg-blue-500", gradient: "from-blue-500 to-blue-700" },
  "digicel_ja": { icon: Smartphone, color: "bg-red-500", gradient: "from-red-500 to-red-700" },
  "jps_ja": { icon: Zap, color: "bg-yellow-500", gradient: "from-yellow-500 to-yellow-700" },
  "nwc_ja": { icon: Droplets, color: "bg-cyan-500", gradient: "from-cyan-500 to-cyan-700" },
};

const BillDetail = ({ user }) => {
  const navigate = useNavigate();
  const { billerId } = useParams();
  const queryClient = useQueryClient();
  const [showPayment, setShowPayment] = useState(false);
  const [showAutoPay, setShowAutoPay] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState("");
  const [step, setStep] = useState(1);
  const [paymentResult, setPaymentResult] = useState(null);

  // Fetch biller details
  const { data: biller, isLoading } = useQuery({
    queryKey: ["biller", billerId],
    queryFn: async () => {
      const response = await axios.get(`${API}/bills/biller/${billerId}`);
      return response.data;
    }
  });

  // Fetch AI insight
  const { data: insight } = useQuery({
    queryKey: ["billInsight", billerId],
    queryFn: async () => {
      const response = await axios.post(`${API}/bills/ai-insight?biller_id=${billerId}`);
      return response.data;
    },
    enabled: !!billerId
  });

  // Fetch JMD wallet
  const { data: wallets } = useQuery({
    queryKey: ["wallets"],
    queryFn: async () => {
      const response = await axios.get(`${API}/wallets`);
      return response.data;
    }
  });

  const jmdWallet = wallets?.find(w => w.currency === "JMD");

  // Delete biller mutation
  const deleteMutation = useMutation({
    mutationFn: async () => {
      await axios.delete(`${API}/bills/biller/${billerId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["myBillers"]);
      toast.success("Biller removed");
      navigate("/bills");
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || "Failed to remove biller");
    }
  });

  // Pay bill mutation
  const payMutation = useMutation({
    mutationFn: async () => {
      const response = await axios.post(`${API}/bills/pay`, {
        biller_id: billerId,
        amount: parseFloat(paymentAmount),
        wallet_id: jmdWallet.wallet_id,
        idempotency_key: uuidv4()
      });
      return response.data;
    },
    onSuccess: (data) => {
      setPaymentResult(data);
      setStep(3);
      queryClient.invalidateQueries(["wallets"]);
      queryClient.invalidateQueries(["transactions"]);
      queryClient.invalidateQueries(["biller", billerId]);
      toast.success("Payment successful!");
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || "Payment failed");
    }
  });

  const formatCurrency = (amount) => {
    return `J$${amount.toLocaleString("en-US", { minimumFractionDigits: 2 })}`;
  };

  const getDaysUntilDue = (dueDate) => {
    if (!dueDate) return null;
    const due = new Date(dueDate);
    const now = new Date();
    return Math.ceil((due - now) / (1000 * 60 * 60 * 24));
  };

  const handlePayNow = () => {
    if (!jmdWallet) {
      toast.error("JMD wallet not found");
      return;
    }
    setPaymentAmount(biller.outstanding_balance.toString());
    setShowPayment(true);
    setStep(1);
  };

  const handleConfirmPayment = () => {
    if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    const total = parseFloat(paymentAmount) + 50; // J$50 fee
    if (jmdWallet.available_balance < total) {
      toast.error("Insufficient funds in JMD wallet");
      return;
    }
    setStep(2);
  };

  const handleExecutePayment = () => {
    payMutation.mutate();
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!biller) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">Biller not found</p>
      </div>
    );
  }

  const iconConfig = providerIcons[biller.provider_id] || { 
    icon: Receipt, 
    color: "bg-gray-500",
    gradient: "from-gray-500 to-gray-700"
  };
  const Icon = iconConfig.icon;
  const daysUntilDue = getDaysUntilDue(biller.due_date);

  return (
    <div className="min-h-screen bg-background" data-testid="bill-detail-page">
      {/* Header */}
      <header className="px-5 pt-4 pb-2 flex items-center justify-between max-w-lg mx-auto">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/bills")}
            className="text-foreground"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="font-heading font-semibold text-xl">Bill Details</h1>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => {
            if (confirm("Remove this biller?")) {
              deleteMutation.mutate();
            }
          }}
          className="text-muted-foreground hover:text-rose-400"
          data-testid="delete-biller-btn"
        >
          <Trash2 className="w-5 h-5" />
        </Button>
      </header>

      <main className="px-5 max-w-lg mx-auto pb-8">
        {/* Biller Card */}
        <div className={`rounded-3xl p-6 mb-6 bg-gradient-to-br ${iconConfig.gradient}`}>
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center">
              <Icon className="w-7 h-7 text-white" />
            </div>
            <div>
              <p className="font-heading font-semibold text-lg text-white">
                {biller.nickname || biller.provider_name}
              </p>
              <p className="text-sm text-white/70">{biller.masked_account}</p>
            </div>
          </div>
          
          {/* Balance */}
          <div className="mt-4 pt-4 border-t border-white/20">
            <p className="text-xs uppercase tracking-widest text-white/50 mb-1">
              Outstanding Balance
            </p>
            <p className="font-mono text-3xl font-bold text-white">
              {formatCurrency(biller.outstanding_balance)}
            </p>
            {daysUntilDue !== null && (
              <p className={`text-sm mt-1 ${
                daysUntilDue <= 5 ? "text-rose-200" : "text-white/70"
              }`}>
                Due in {daysUntilDue} days
              </p>
            )}
          </div>
        </div>

        {/* AI Insights */}
        {insight && insight.insights && insight.insights.length > 0 && (
          <div className="glass rounded-2xl p-4 mb-6">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4 text-primary" />
              <span className="text-xs uppercase tracking-widest text-muted-foreground/70">
                AI Insights
              </span>
            </div>
            <div className="space-y-2">
              {insight.insights.map((text, index) => (
                <p key={index} className="text-sm text-muted-foreground">
                  {text}
                </p>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="space-y-3 mb-6">
          <Button
            className="w-full btn-primary h-14 text-base"
            onClick={handlePayNow}
            data-testid="pay-now-btn"
          >
            Pay Now
          </Button>
          
          <div className="grid grid-cols-2 gap-3">
            <Button
              variant="outline"
              className="btn-secondary h-12 text-sm"
              onClick={() => setShowSchedule(true)}
              data-testid="schedule-btn"
            >
              <CalendarClock className="w-4 h-4 mr-2" />
              Schedule
            </Button>
            <Button
              variant="outline"
              className={`btn-secondary h-12 text-sm ${biller.autopay_enabled ? "border-primary/50 bg-primary/10" : ""}`}
              onClick={() => setShowAutoPay(true)}
              data-testid="auto-pay-btn"
            >
              <Clock className="w-4 h-4 mr-2" />
              {biller.autopay_enabled ? "Auto-Pay ✓" : "Auto-Pay"}
            </Button>
          </div>
        </div>

        {/* Bill Info */}
        <div className="glass rounded-2xl p-4">
          <p className="text-xs uppercase tracking-widest text-muted-foreground/70 mb-4">
            Bill Information
          </p>
          <div className="space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Provider</span>
              <span className="text-foreground">{biller.provider_name}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Account</span>
              <span className="text-foreground font-mono">{biller.masked_account}</span>
            </div>
            {biller.last_payment_date && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Last Payment</span>
                <span className="text-foreground">
                  {new Date(biller.last_payment_date).toLocaleDateString()}
                </span>
              </div>
            )}
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Fee</span>
              <span className="text-foreground">J$50 per payment</span>
            </div>
          </div>
        </div>
      </main>

      {/* Payment Modal */}
      {showPayment && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center"
          onClick={() => !payMutation.isPending && setShowPayment(false)}
        >
          <div 
            className="w-full max-w-lg bg-card/95 backdrop-blur-2xl rounded-t-3xl sm:rounded-3xl 
              border-t sm:border border-white/10 p-6 animate-slide-up max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Step 1: Enter Amount */}
            {step === 1 && (
              <>
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-heading font-semibold text-lg">Pay Bill</h3>
                  <button 
                    onClick={() => setShowPayment(false)}
                    className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center text-muted-foreground"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div className="glass rounded-2xl p-4 flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl ${iconConfig.color} flex items-center justify-center`}>
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="font-medium text-foreground">{biller.provider_name}</p>
                      <p className="text-xs text-muted-foreground">{biller.masked_account}</p>
                    </div>
                  </div>

                  <div>
                    <label className="text-sm text-muted-foreground">Amount</label>
                    <div className="relative mt-2">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-xl text-muted-foreground">
                        J$
                      </span>
                      <Input
                        type="number"
                        className="input-glass pl-12 text-2xl font-mono h-14"
                        value={paymentAmount}
                        onChange={(e) => setPaymentAmount(e.target.value)}
                        data-testid="payment-amount-input"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      Outstanding: {formatCurrency(biller.outstanding_balance)}
                    </p>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      className="flex-1 btn-secondary"
                      onClick={() => setPaymentAmount(biller.outstanding_balance.toString())}
                    >
                      Full Amount
                    </Button>
                    <Button
                      variant="outline"
                      className="flex-1 btn-secondary"
                      onClick={() => setPaymentAmount((biller.outstanding_balance / 2).toFixed(2))}
                    >
                      Half
                    </Button>
                  </div>

                  <Button
                    className="w-full btn-primary h-14 text-base"
                    onClick={handleConfirmPayment}
                    data-testid="continue-payment-btn"
                  >
                    Continue
                  </Button>
                </div>
              </>
            )}

            {/* Step 2: Confirm */}
            {step === 2 && (
              <>
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-heading font-semibold text-lg">Confirm Payment</h3>
                  <button 
                    onClick={() => setStep(1)}
                    className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center text-muted-foreground"
                  >
                    <ArrowLeft className="w-4 h-4" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div className="glass rounded-2xl p-4 space-y-3">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Biller</span>
                      <span className="text-foreground">{biller.provider_name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Account</span>
                      <span className="text-foreground font-mono">{biller.masked_account}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Amount</span>
                      <span className="text-foreground font-mono">{formatCurrency(parseFloat(paymentAmount))}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Fee</span>
                      <span className="text-foreground font-mono">J$50.00</span>
                    </div>
                    <div className="border-t border-white/10 pt-3 flex justify-between font-semibold">
                      <span className="text-foreground">Total</span>
                      <span className="text-primary font-mono">
                        {formatCurrency(parseFloat(paymentAmount) + 50)}
                      </span>
                    </div>
                  </div>

                  <div className="glass rounded-2xl p-4 flex items-center gap-3">
                    <AlertCircle className="w-5 h-5 text-yellow-400" />
                    <p className="text-xs text-muted-foreground">
                      Paying from JMD Wallet (Balance: {formatCurrency(jmdWallet?.available_balance || 0)})
                    </p>
                  </div>

                  <Button
                    className="w-full btn-primary h-14 text-base"
                    onClick={handleExecutePayment}
                    disabled={payMutation.isPending}
                    data-testid="confirm-payment-btn"
                  >
                    {payMutation.isPending ? "Processing..." : "Confirm & Pay"}
                  </Button>
                </div>
              </>
            )}

            {/* Step 3: Success */}
            {step === 3 && paymentResult && (
              <div className="text-center">
                <div className="w-20 h-20 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-6 animate-pulse-glow">
                  <Check className="w-10 h-10 text-primary" />
                </div>
                
                <h2 className="font-heading font-bold text-2xl mb-2">Payment Successful!</h2>
                <p className="text-muted-foreground mb-6">
                  Your bill has been paid
                </p>

                <div className="glass rounded-2xl p-4 text-left space-y-3 mb-6">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Confirmation</span>
                    <span className="text-primary font-mono">{paymentResult.confirmation_number}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Amount Paid</span>
                    <span className="text-foreground font-mono">{formatCurrency(paymentResult.amount)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Fee</span>
                    <span className="text-foreground font-mono">{formatCurrency(paymentResult.fee)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Date</span>
                    <span className="text-foreground">
                      {new Date(paymentResult.created_at).toLocaleString()}
                    </span>
                  </div>
                </div>

                <Button
                  className="w-full btn-primary h-14 text-base"
                  onClick={() => {
                    setShowPayment(false);
                    navigate("/bills");
                  }}
                  data-testid="done-btn"
                >
                  Done
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Auto-Pay Modal */}
      {showAutoPay && biller && (
        <AutoPayModal
          biller={biller}
          onClose={() => {
            setShowAutoPay(false);
            queryClient.invalidateQueries(["biller", billerId]);
          }}
        />
      )}

      {/* Schedule Payment Modal */}
      {showSchedule && biller && (
        <SchedulePaymentModal
          biller={biller}
          onClose={() => setShowSchedule(false)}
        />
      )}
    </div>
  );
};

export default BillDetail;
